import { GrindingOption, isAllowedAdditionalProcess, isAttachments, isComment, isCountersinking, isFixedRotations, isFlipSideUsage, isMechanicalGrinding, isTestReport, isThreading, } from "./articleeditdata";
import { articleSignatureNodeObject, isInstanceOf, isNodeIdKey, isRootIdKey, isTypeOrUndefined, isVertexKey, } from "./aux";
import { isManufacturingState, isScalePrice, isShopTappingNodeData, isTubeProfileEntry, isTubeProfileGeometry, } from "./graph";
import { isValid, } from "./material";
import { Level, ProcessType, WorkStepType, } from "./wsi4_interfaces";
import { isArray, isBoolean, isNumber, isString, isTubeMaterial, isTubeSpecification, isWorkStepType, isProcessType, isTubeProfileGeometryRectangular, isTubeProfileGeometryCircular, exhaustiveStringTuple, computeResultingLevel, } from "./wsi4_typeguards";
const generalTypeGuardMap = {
    articleExternalDrawingNumber: isString,
    articleExternalPartNumber: isString,
    articleExternalRevisionNumber: isString,
    articleName: isString,
    multiplicity: isNumber,
    nodeIdKey: isNodeIdKey,
    png: (arg) => isTypeOrUndefined(arg, isString),
    rootIdKey: isRootIdKey,
    svg: (arg) => isTypeOrUndefined(arg, isString),
    vertexKey: isVertexKey,
    actualManufacturingState: isManufacturingState,
    virtualManufacturingState: isManufacturingState,
    gltf: (arg) => isTypeOrUndefined(arg, isString),
    processType: isProcessType,
    problematicGeometries: isBoolean,
};
export function isGeneral(arg) {
    return isInstanceOf(arg, generalTypeGuardMap);
}
export function isCuttingGasId(arg) {
    return isString(arg);
}
export function isSheetMaterialId(arg) {
    return isString(arg);
}
export function isMaterialIdWithCuttingGasId(arg) {
    const typeGuardMap = {
        sheetMaterialId: isSheetMaterialId,
        cuttingGasId: isCuttingGasId,
    };
    return isInstanceOf(arg, typeGuardMap);
}
export var PartState;
(function (PartState) {
    PartState["userDefinedTube"] = "userDefinedTube";
    PartState["sheetCuttingPart"] = "sheetCuttingPart";
    PartState["sheetBendingPart"] = "sheetBendingPart";
    PartState["manufacturableWithWarning"] = "manufacturableWithWarning";
    PartState["notManufacturable"] = "notManufacturable";
    PartState["purchasedPart"] = "purchasedPart";
})(PartState || (PartState = {}));
export function isPartState(t) {
    return t === PartState.userDefinedTube
        || t === PartState.sheetCuttingPart
        || t === PartState.sheetBendingPart
        || t === PartState.manufacturableWithWarning
        || t === PartState.notManufacturable
        || t === PartState.purchasedPart;
}
const manufacturablePartBaseTypeGuardMap = {
    ...generalTypeGuardMap,
    manufactureTag: (arg) => arg === "Manufacturable",
    approxPrice: (arg) => isTypeOrUndefined(arg, isNumber),
    attachments: isAttachments,
    comment: isComment,
    countersinking: (arg) => isTypeOrUndefined(arg, isCountersinking),
    fixedRotations: (arg) => isTypeOrUndefined(arg, isFixedRotations),
    flipSideUsage: isFlipSideUsage,
    materialIdWithCuttingGasId: isMaterialIdWithCuttingGasId,
    mechanicalGrinding: (arg) => isTypeOrUndefined(arg, isMechanicalGrinding),
    testReport: (arg) => isTypeOrUndefined(arg, isTestReport),
    thickness: isNumber,
    threading: (arg) => isTypeOrUndefined(arg, isThreading),
    vertexKeyOfSheetCutting: isVertexKey,
    partState: isPartState,
    width: isNumber,
    height: isNumber,
    technicalDrawings: (arg) => isArray(arg, isString),
    tapping: (arg) => isTypeOrUndefined(arg, isShopTappingNodeData),
    forcedProcessType: isBoolean,
    otherNodeHasTestReport: isBoolean,
};
export function isManufacturablePartBase(arg) {
    return isInstanceOf(arg, manufacturablePartBaseTypeGuardMap);
}
const notManufacturablePartBaseTypeGuardMap = {
    ...generalTypeGuardMap,
    manufactureTag: (arg) => arg === "NotManufacturable",
    approxPrice: (arg) => isTypeOrUndefined(arg, isNumber),
    partState: (arg) => arg === PartState.notManufacturable,
    width: isNumber,
    height: isNumber,
    thickness: isNumber,
};
export function isNotManufacturablePartBase(arg) {
    return isInstanceOf(arg, notManufacturablePartBaseTypeGuardMap);
}
export function isManufacturePart(arg) {
    return isManufacturablePartBase(arg) || isNotManufacturablePartBase(arg);
}
const purchasedPartBaseTypeGuardMap = {
    ...generalTypeGuardMap,
    price: (arg) => isTypeOrUndefined(arg, isNumber),
    purchasedPart: (arg) => arg === true,
    canForce: isBoolean,
    partState: (arg) => arg === PartState.purchasedPart,
};
export function isPurchasedPartBase(arg) {
    return isInstanceOf(arg, purchasedPartBaseTypeGuardMap);
}
export function isManufacturableAssemblyPart(arg) {
    const manufacturableAssemblyPartTypeGuardMap = {
        ...manufacturablePartBaseTypeGuardMap,
        assemblyVertexKey: isVertexKey,
    };
    return isInstanceOf(arg, manufacturableAssemblyPartTypeGuardMap);
}
export function isNotManufacturableAssemblyPart(arg) {
    return isInstanceOf(arg, {
        ...notManufacturablePartBaseTypeGuardMap,
        assemblyVertexKey: isVertexKey,
    });
}
export function isPurchasedAssemblyPart(arg) {
    const purchasedAssemblyPartTypeGuardMap = {
        ...purchasedPartBaseTypeGuardMap,
        assemblyVertexKey: isVertexKey,
    };
    return isInstanceOf(arg, purchasedAssemblyPartTypeGuardMap);
}
export function isAssemblyPart(arg) {
    return isManufacturableAssemblyPart(arg) || isNotManufacturableAssemblyPart(arg) || isPurchasedAssemblyPart(arg);
}
export function isManufacturableSinglePart(arg) {
    const manufacturableSinglePartTypeGuardMap = {
        ...manufacturablePartBaseTypeGuardMap,
        lastWorkStepType: isWorkStepType,
        scalePrices: (arg) => isArray(arg, isScalePrice),
        singlePart: (arg) => arg === true,
    };
    return isInstanceOf(arg, manufacturableSinglePartTypeGuardMap);
}
export function isNotManufacturableSinglePart(arg) {
    const manufacturableSinglePartTypeGuardMap = {
        ...notManufacturablePartBaseTypeGuardMap,
        lastWorkStepType: isWorkStepType,
        singlePart: (arg) => arg === true,
    };
    return isInstanceOf(arg, manufacturableSinglePartTypeGuardMap);
}
export function isPurchasedSinglePart(arg) {
    const purchasedSinglePartTypeGuardMap = {
        ...purchasedPartBaseTypeGuardMap,
        singlePart: (arg) => arg === true,
    };
    return isInstanceOf(arg, purchasedSinglePartTypeGuardMap);
}
export function isUserDefinedTube(arg) {
    const typeGuardMap = {
        ...generalTypeGuardMap,
        attachments: isAttachments,
        dimX: isNumber,
        material: (arg) => isTypeOrUndefined(arg, isTubeMaterial),
        partState: isPartState,
        approxPrice: (arg) => isTypeOrUndefined(arg, isNumber),
        profile: (arg) => isTypeOrUndefined(arg, isTubeProfileEntry),
        singlePart: (arg) => arg === true,
        specification: (arg) => isTypeOrUndefined(arg, isTubeSpecification),
        tubeId: (arg) => isTypeOrUndefined(arg, isString),
        processId: isString,
        geometry: isTubeProfileGeometry,
    };
    return isInstanceOf(arg, typeGuardMap);
}
export function isSinglePart(arg) {
    return isManufacturableSinglePart(arg) || isNotManufacturableSinglePart(arg) || isPurchasedSinglePart(arg) || isUserDefinedTube(arg);
}
export function isPart(arg) {
    return isSinglePart(arg) || isAssemblyPart(arg);
}
export function isManufacturablePart(arg) {
    return isManufacturableSinglePart(arg) || isManufacturableAssemblyPart(arg);
}
export function isNotManufacturablePart(arg) {
    return isNotManufacturableSinglePart(arg) || isNotManufacturableAssemblyPart(arg);
}
export function isPurchasedPart(arg) {
    return isPurchasedSinglePart(arg) || isPurchasedAssemblyPart(arg);
}
export function isAssembly(arg) {
    const assemblyTypeGuardMap = {
        ...generalTypeGuardMap,
        lastWorkStepType: isWorkStepType,
        parts: (t) => isArray(t, isPart),
        approxPrice: (arg) => isTypeOrUndefined(arg, isNumber),
        scalePrices: (arg) => isArray(arg, isScalePrice),
    };
    return isInstanceOf(arg, assemblyTypeGuardMap);
}
export function isProjectEntry(arg) {
    return isPart(arg) || isAssembly(arg);
}
export function isProject(arg) {
    const projectTypeGuardMap = {
        singleParts: (t) => isArray(t, isSinglePart),
        assemblies: (t) => isArray(t, isAssembly),
    };
    return isInstanceOf(arg, projectTypeGuardMap);
}
function createGeneral(article, shopResources) {
    const signatureNode = articleSignatureNodeObject(article);
    return {
        articleExternalDrawingNumber: article.externalDrawingNumber,
        articleExternalPartNumber: article.externalPartNumber,
        articleExternalRevisionNumber: article.externalRevisionNumber,
        articleName: signatureNode.name,
        multiplicity: article.multiplicity,
        nodeIdKey: signatureNode.nodeIdKey,
        png: shopResources.pngs[signatureNode.nodeIdKey],
        rootIdKey: signatureNode.rootIdKey,
        svg: shopResources.svgs[signatureNode.nodeIdKey],
        vertexKey: signatureNode.vertex,
        actualManufacturingState: article.nodes.flatMap(node => node.actualManufacturingState),
        virtualManufacturingState: article.nodes.flatMap(node => node.virtualManufacturingState),
        gltf: shopResources.problematicGeometryGlfts[signatureNode.nodeIdKey] === undefined ? shopResources.gltfs[signatureNode.nodeIdKey] : shopResources.problematicGeometryGlfts[signatureNode.nodeIdKey],
        processType: signatureNode.processType,
        problematicGeometries: shopResources.problematicGeometryGlfts[signatureNode.nodeIdKey] !== undefined,
    };
}
function createNotManufacturableBase(article, shopResources) {
    const signatureNode = articleSignatureNodeObject(article);
    if (signatureNode.width === undefined) {
        throw Error("Missing width.");
    }
    if (signatureNode.height === undefined) {
        throw Error("Missing height.");
    }
    const thickness = signatureNode.thickness;
    if (thickness === undefined) {
        throw Error("Missing thickness.");
    }
    return {
        ...createGeneral(article, shopResources),
        manufactureTag: "NotManufacturable",
        approxPrice: article.approxPrice,
        partState: PartState.notManufacturable,
        width: signatureNode.width,
        height: signatureNode.height,
        thickness: thickness,
    };
}
function createManufactureBase(graph, article, shopConfig, shopResources) {
    if (article.nodes.every(n => n.workStepType !== WorkStepType.sheetCutting)) {
        return createNotManufacturableBase(article, shopResources);
    }
    return createManufacturablePartBase(graph, article, shopConfig, shopResources);
}
function partState(signatureNode, m) {
    const level = computeResultingLevel(m);
    switch (level) {
        case Level.info: {
            if (signatureNode.workStepType === ProcessType.tube || signatureNode.workStepType === ProcessType.tubeCutting) {
                return PartState.userDefinedTube;
            }
            if (signatureNode.workStepType === WorkStepType.sheetBending) {
                return PartState.sheetBendingPart;
            }
            if (signatureNode.workStepType === WorkStepType.sheetCutting) {
                return PartState.sheetCuttingPart;
            }
            return PartState.purchasedPart;
        }
        case Level.warning: return PartState.manufacturableWithWarning;
        case Level.recoverableError: return PartState.notManufacturable;
        case Level.fatalError: return PartState.notManufacturable;
    }
}
function articleManufacturingState(article) {
    return article.nodes.reduce((m, n) => [
        ...m,
        ...n.actualManufacturingState,
    ], []);
}
export function articlePartState(article) {
    return partState(articleSignatureNodeObject(article), articleManufacturingState(article));
}
function createManufacturablePartBase(graph, article, shopConfig, shopResources) {
    var _a;
    const signatureNode = articleSignatureNodeObject(article);
    const sheetCuttingNode = article.nodes.find(n => n.workStepType === WorkStepType.sheetCutting);
    if (sheetCuttingNode === undefined) {
        throw Error("Missing sheetCutting node");
    }
    const material = signatureNode.userData.material;
    if (material === undefined) {
        throw Error("Missing material.");
    }
    if (sheetCuttingNode.cuttingGasId === undefined) {
        throw Error("Missing cuttingGas");
    }
    if (signatureNode.flipSide === undefined) {
        throw Error("Missing flip side.");
    }
    if (signatureNode.comment === undefined) {
        throw Error("Missing comment.");
    }
    const thickness = signatureNode.thickness;
    if (thickness === undefined) {
        throw Error("Missing thickness.");
    }
    const attachments = shopResources.attachments[signatureNode.nodeIdKey];
    if (attachments === undefined) {
        throw Error("Missing attachments");
    }
    const computeGrindingOption = (nodes) => {
        const deburring = nodes.find(n => n.processType === ProcessType.manualMechanicalDeburring || n.processType === ProcessType.automaticMechanicalDeburring);
        const slideGrinding = nodes.find(n => n.processType === ProcessType.slideGrinding);
        if (deburring && slideGrinding) {
            throw Error("Slide grinding and deburring should be exclusive.");
        }
        if (deburring) {
            if (deburring.userData.doubleSidedMechanicalDeburring === undefined) {
                return GrindingOption.none;
            }
            const doubleSided = deburring.userData.doubleSidedMechanicalDeburring;
            return doubleSided ? GrindingOption.doubleSidedMechanicalDeburring : GrindingOption.singleSidedMechanicalDeburring;
        }
        if (slideGrinding) {
            return GrindingOption.slideGrinding;
        }
        return GrindingOption.none;
    };
    const grindingOption = isAllowedAdditionalProcess("mechanicalGrinding", shopConfig) ? computeGrindingOption(article.nodes) : undefined;
    const deburringPreviewPrices = (() => {
        if (grindingOption !== undefined) {
            if (sheetCuttingNode.deburringPreviewPrices === undefined) {
                throw Error("Missing deburring preview prices");
            }
            if (sheetCuttingNode.deburringPreviewPrices.length !== 2) {
                throw Error("Wrong size of deburring preview prices");
            }
            return sheetCuttingNode.deburringPreviewPrices;
        }
        else {
            return [
                0.,
                0.,
            ];
        }
    })();
    const slideGrindingPrice = (_a = sheetCuttingNode.slideGrindingPreviewPrice) !== null && _a !== void 0 ? _a : 0.;
    const mechanicalGrinding = grindingOption === undefined
        ? undefined
        : {
            option: grindingOption,
            prices: {
                none: 0,
                singleSidedMechanicalDeburring: deburringPreviewPrices[0],
                doubleSidedMechanicalDeburring: deburringPreviewPrices[1],
                slideGrinding: slideGrindingPrice,
            },
        };
    const computeThreading = (nodes) => {
        const n = nodes.find(n => n.processType === ProcessType.userDefinedThreading);
        if (n === undefined) {
            return {
                numThreads: 0,
            };
        }
        if (n.numThreads === undefined) {
            throw Error("Missing numThreads");
        }
        return {
            numThreads: n.numThreads,
        };
    };
    const threading = isAllowedAdditionalProcess("threading", shopConfig) ? computeThreading(article.nodes) : undefined;
    const potentialTappingData = shopResources.sheetTappingNodeData.find(n => n.laserSheetCuttingVertexKey === sheetCuttingNode.vertex);
    const noTappingData = {
        svg: "",
        nodeIdKey: "",
        selection: [],
        candidates: [],
        laserSheetCuttingVertexKey: "",
    };
    const tapping = potentialTappingData === undefined ? noTappingData : potentialTappingData;
    if (!isShopTappingNodeData(tapping)) {
        throw Error("Wrong type.");
    }
    const computeCountersinking = (nodes) => {
        const n = nodes.find(n => n.processType === ProcessType.userDefinedCountersinking);
        if (n === undefined) {
            return {
                numCounterSinks: 0,
            };
        }
        if (n.numCountersinks === undefined) {
            throw Error("Missing numCountersinks");
        }
        return {
            numCounterSinks: n.numCountersinks,
        };
    };
    const countersinking = isAllowedAdditionalProcess("countersinking", shopConfig) ? computeCountersinking(article.nodes) : undefined;
    if (signatureNode.width === undefined) {
        throw Error("Missing width.");
    }
    if (signatureNode.height === undefined) {
        throw Error("Missing height.");
    }
    const technicalDrawings = shopResources.technicalDrawings[signatureNode.nodeIdKey];
    const otherNodeHasTestReport = (() => {
        if (!shopConfig.settings.testReportRequired || signatureNode.testReportRequired === undefined) {
            return false;
        }
        if (signatureNode.testReportRequired) {
            return false;
        }
        const sheetNode = article.nodes.find(n => n.workStepType === "sheet");
        if (sheetNode === undefined) {
            return false;
        }
        const pred = (a) => a.nodes.some(n => n.vertex === sheetNode.vertex) && articleSignatureNodeObject(a).testReportRequired;
        return graph.some(a => pred(a) || a.subArticles.some(s => pred(s)));
    })();
    return {
        ...createGeneral(article, shopResources),
        manufactureTag: "Manufacturable",
        attachments: {
            attachments: attachments,
        },
        comment: {
            comment: signatureNode.comment,
        },
        countersinking: countersinking,
        flipSideUsage: {
            flipSide: signatureNode.flipSide,
        },
        fixedRotations: !shopConfig.settings.fixedRotationsEnabled || sheetCuttingNode.fixedRotations === undefined
            ? undefined
            : {
                rotations: sheetCuttingNode.fixedRotations,
            },
        materialIdWithCuttingGasId: {
            sheetMaterialId: material.identifier,
            cuttingGasId: sheetCuttingNode.cuttingGasId,
        },
        mechanicalGrinding: mechanicalGrinding,
        testReport: !shopConfig.settings.testReportRequired || signatureNode.testReportRequired === undefined
            ? undefined
            : {
                required: signatureNode.testReportRequired,
            },
        thickness: thickness,
        threading: threading,
        vertexKeyOfSheetCutting: sheetCuttingNode.vertex,
        partState: articlePartState(article),
        approxPrice: article.approxPrice,
        width: signatureNode.width,
        height: signatureNode.height,
        technicalDrawings: technicalDrawings === undefined ? [] : technicalDrawings,
        tapping: tapping,
        forcedProcessType: signatureNode.forcedProcessType,
        otherNodeHasTestReport: otherNodeHasTestReport,
    };
}
function createPurcharsedPartBase(article, shopResources) {
    const signatureNode = articleSignatureNodeObject(article);
    const canForceSheetPartData = shopResources.canForceSheetPartData.find(c => c.nodeIdKey === signatureNode.nodeIdKey);
    return {
        ...createGeneral(article, shopResources),
        price: article.approxPrice,
        purchasedPart: true,
        canForce: canForceSheetPartData === undefined ? false : canForceSheetPartData.canForce,
        partState: PartState.purchasedPart,
    };
}
function createManufacturableSinglePart(article, signatureNode, base) {
    return {
        ...base,
        lastWorkStepType: signatureNode.workStepType,
        scalePrices: article.scalePrices,
        singlePart: true,
    };
}
function createNotManufacturableSinglePart(signatureNode, base) {
    return {
        ...base,
        lastWorkStepType: signatureNode.workStepType,
        singlePart: true,
    };
}
function createManufactureSinglePart(graph, article, shopConfig, shopResources) {
    const n = articleSignatureNodeObject(article);
    const base = createManufactureBase(graph, article, shopConfig, shopResources);
    if (isManufacturablePartBase(base)) {
        return createManufacturableSinglePart(article, n, base);
    }
    return createNotManufacturableSinglePart(n, base);
}
function createPurchasedSinglePart(article, shopResources) {
    return {
        ...createPurcharsedPartBase(article, shopResources),
        singlePart: true,
    };
}
function equalNumbers(lhs, rhs) {
    return Math.abs(lhs - rhs) < 1e-5;
}
function areEqualTubeCuttingGeometries(lhs, rhs) {
    if (isTubeProfileGeometryRectangular(lhs.content)) {
        if (!isTubeProfileGeometryRectangular(rhs.content)) {
            return false;
        }
        const keys = exhaustiveStringTuple()("dimY", "thickness", "dimZ");
        for (const k of keys) {
            if (!equalNumbers(lhs.content[k], rhs.content[k])) {
                return false;
            }
        }
        return true;
    }
    if (!isTubeProfileGeometryCircular(lhs.content)) {
        throw Error("Wrong type.");
    }
    if (!isTubeProfileGeometryCircular(rhs.content)) {
        return false;
    }
    const keys = exhaustiveStringTuple()("outerRadius", "thickness");
    for (const k of keys) {
        if (!equalNumbers(lhs.content[k], rhs.content[k])) {
            return false;
        }
    }
    return true;
}
function createUserDefinedTube(article, shopConfig, shopResources) {
    const n = articleSignatureNodeObject(article);
    const tubeCuttingData = shopResources.tubeCuttingData.find(t => t.nodeIdKey === n.nodeIdKey);
    if (tubeCuttingData === undefined) {
        throw Error("Missing tubeCuttingData.");
    }
    const material = shopConfig.tubeMaterials.find(t => tubeCuttingData.tubeMaterialId === t.identifier);
    const specification = shopConfig.tubeSpecifications.find(t => tubeCuttingData.tubeSpecificationId === t.identifier);
    const profile = shopConfig.tubeProfiles.find(t => areEqualTubeCuttingGeometries(tubeCuttingData.tubeProfileGeometry, { type: t.geometryType, content: t.geometry }));
    const tube = profile === undefined
        ? undefined
        : shopConfig.tubes.find(t => t.tubeSpecificationId === tubeCuttingData.tubeSpecificationId && t.tubeMaterialId === tubeCuttingData.tubeMaterialId && t.tubeProfileId === profile.identifier);
    const dimX = Math.round(tubeCuttingData.dimX * 100) / 100;
    const attachments = shopResources.attachments[n.nodeIdKey];
    if (attachments === undefined) {
        throw Error("Missing attachments");
    }
    return {
        ...createGeneral(article, shopResources),
        attachments: {
            attachments: attachments,
        },
        dimX: dimX,
        material: material,
        partState: articlePartState(article),
        profile: profile,
        approxPrice: article.approxPrice,
        singlePart: true,
        specification: specification,
        processId: n.processId,
        tubeId: tube === undefined ? undefined : tube.identifier,
        geometry: tubeCuttingData.tubeProfileGeometry,
    };
}
function createSinglePart(graph, article, shopConfig, shopResources) {
    const n = articleSignatureNodeObject(article);
    if (n.processType === ProcessType.externalPart) {
        return createPurchasedSinglePart(article, shopResources);
    }
    if (n.workStepType === WorkStepType.tube || n.workStepType === WorkStepType.tubeCutting) {
        return createUserDefinedTube(article, shopConfig, shopResources);
    }
    return createManufactureSinglePart(graph, article, shopConfig, shopResources);
}
function createPurchasedAssemblyPart(article, assemblyVertexKey, shopResources) {
    return {
        ...createPurcharsedPartBase(article, shopResources),
        assemblyVertexKey: assemblyVertexKey,
    };
}
function createManufacturableAssemblyPart(assemblyVertexKey, base) {
    return {
        ...base,
        assemblyVertexKey: assemblyVertexKey,
    };
}
function createNotManufacturableAssemblyPart(assemblyVertexKey, base) {
    return {
        ...base,
        assemblyVertexKey: assemblyVertexKey,
    };
}
function createManufactureAssemblyPart(graph, article, assemblyVertexKey, shopConfig, shopResources) {
    const base = createManufactureBase(graph, article, shopConfig, shopResources);
    if (isManufacturablePartBase(base)) {
        return createManufacturableAssemblyPart(assemblyVertexKey, base);
    }
    return createNotManufacturableAssemblyPart(assemblyVertexKey, base);
}
function createAssemblyPart(graph, article, assemblyVertexKey, shopConfig, shopResources) {
    const n = articleSignatureNodeObject(article);
    if (n.processType === ProcessType.externalPart) {
        return createPurchasedAssemblyPart(article, assemblyVertexKey, shopResources);
    }
    return createManufactureAssemblyPart(graph, article, assemblyVertexKey, shopConfig, shopResources);
}
function createAssembly(graph, article, shopConfig, shopResources) {
    const n = articleSignatureNodeObject(article);
    const approxPrice = article.subArticles.reduce((res, a) => res + (a.approxPrice === undefined ? 0 : a.approxPrice), 0);
    return {
        ...createGeneral(article, shopResources),
        parts: article.subArticles.map(s => createAssemblyPart(graph, s, n.vertex, shopConfig, shopResources)),
        lastWorkStepType: n.workStepType,
        approxPrice: approxPrice,
        scalePrices: article.scalePrices,
    };
}
export function createProject(graph, shopConfig, shopResources) {
    const project = {
        singleParts: [],
        assemblies: [],
    };
    for (const article of graph) {
        if (article.subArticles.length === 0) {
            project.singleParts.push(createSinglePart(graph, article, shopConfig, shopResources));
        }
        else {
            project.assemblies.push(createAssembly(graph, article, shopConfig, shopResources));
        }
    }
    return project;
}
function articleEditDataForGeneral(entry) {
    return [
        {
            key: "articleName",
            value: entry.articleName,
            vertexKey: entry.vertexKey,
        },
        {
            key: "articleExternalDrawingNumber",
            value: entry.articleExternalDrawingNumber,
            vertexKey: entry.vertexKey,
        },
        {
            key: "articleExternalPartNumber",
            value: entry.articleExternalPartNumber,
            vertexKey: entry.vertexKey,
        },
        {
            key: "articleExternalRevisionNumber",
            value: entry.articleExternalRevisionNumber,
            vertexKey: entry.vertexKey,
        },
    ];
}
function articleEditDataForAssembly(assembly) {
    return [
        ...articleEditDataForGeneral(assembly),
        {
            key: "multiplicity",
            value: assembly.multiplicity,
            vertexKey: assembly.vertexKey,
        },
    ];
}
function convertToMaterialWithGas(materialIdWithCuttingGasId, thickness, shopConfig) {
    const material = shopConfig.material.find(m => m.identifier === materialIdWithCuttingGasId.sheetMaterialId);
    if (material === undefined) {
        throw Error("Missing material.");
    }
    return {
        material: material,
        cuttingGasId: materialIdWithCuttingGasId.cuttingGasId,
        invalid: !isValid(shopConfig, [thickness], materialIdWithCuttingGasId.sheetMaterialId, materialIdWithCuttingGasId.cuttingGasId),
    };
}
function articleEditDataForManufacturableBase(entry, shopConfig) {
    const entries = [
        ...articleEditDataForGeneral(entry),
        {
            key: "attachments",
            value: entry.attachments,
            vertexKey: entry.vertexKey,
        },
        {
            key: "comment",
            value: entry.comment,
            vertexKey: entry.vertexKey,
        },
        {
            key: "flipSideUsage",
            value: entry.flipSideUsage,
            vertexKey: entry.vertexKey,
        },
        {
            key: "materialWithGas",
            value: convertToMaterialWithGas(entry.materialIdWithCuttingGasId, entry.thickness, shopConfig),
            vertexKey: entry.vertexKey,
        },
    ];
    if (entry.countersinking !== undefined) {
        entries.push({
            key: "countersinking",
            value: entry.countersinking,
            vertexKey: entry.vertexKeyOfSheetCutting,
        });
    }
    if (entry.fixedRotations !== undefined) {
        entries.push({
            key: "fixedRotations",
            value: entry.fixedRotations,
            vertexKey: entry.vertexKeyOfSheetCutting,
        });
    }
    if (entry.mechanicalGrinding !== undefined) {
        entries.push({
            key: "mechanicalGrinding",
            value: entry.mechanicalGrinding,
            vertexKey: entry.vertexKeyOfSheetCutting,
        });
    }
    if (entry.testReport !== undefined) {
        entries.push({
            key: "testReport",
            value: entry.testReport,
            vertexKey: entry.vertexKey,
        });
    }
    if (entry.threading !== undefined) {
        entries.push({
            key: "threading",
            value: entry.threading,
            vertexKey: entry.vertexKeyOfSheetCutting,
        });
    }
    if (entry.tapping !== undefined) {
        entries.push({
            key: "tapping",
            value: entry.tapping,
            vertexKey: entry.vertexKeyOfSheetCutting,
        });
    }
    return entries;
}
function articleEditDataForManufacturableAssemblyPart(entry, shopConfig) {
    return articleEditDataForManufacturableBase(entry, shopConfig);
}
function articleEditDataForManufacturableSinglePart(entry, shopConfig) {
    const entries = [
        ...articleEditDataForManufacturableBase(entry, shopConfig),
        {
            key: "multiplicity",
            value: entry.multiplicity,
            vertexKey: entry.vertexKey,
        },
    ];
    if (entry.lastWorkStepType === WorkStepType.sheetCutting) {
        entries.push({
            key: "thickness",
            value: entry.thickness,
            vertexKey: entry.vertexKey,
        });
    }
    return entries;
}
function articleEditDataForUserDefinedTube(entry) {
    const a = [
        ...articleEditDataForGeneral(entry),
        {
            key: "multiplicity",
            value: entry.multiplicity,
            vertexKey: entry.vertexKey,
        },
        {
            key: "attachments",
            value: entry.attachments,
            vertexKey: entry.vertexKey,
        },
    ];
    if (entry.tubeId !== undefined) {
        a.push({
            key: "tubeCuttingData",
            value: {
                tubeId: entry.tubeId,
                processId: entry.processId,
            },
            vertexKey: entry.vertexKey,
        });
    }
    return a;
}
function articleEditDataForNotManufacturableAssemblyPart(entry) {
    return articleEditDataForGeneral(entry);
}
function articleEditDataForNotManufacturableSinglePart(entry) {
    return [
        ...articleEditDataForGeneral(entry),
        {
            key: "multiplicity",
            value: entry.multiplicity,
            vertexKey: entry.vertexKey,
        },
    ];
}
export function articleEditData(projectEntry, shopConfig) {
    if (isPurchasedPart(projectEntry)) {
        return [];
    }
    else if (isManufacturableAssemblyPart(projectEntry)) {
        return articleEditDataForManufacturableAssemblyPart(projectEntry, shopConfig);
    }
    else if (isManufacturableSinglePart(projectEntry)) {
        return articleEditDataForManufacturableSinglePart(projectEntry, shopConfig);
    }
    else if (isUserDefinedTube(projectEntry)) {
        return articleEditDataForUserDefinedTube(projectEntry);
    }
    else if (isNotManufacturableSinglePart(projectEntry)) {
        return articleEditDataForNotManufacturableSinglePart(projectEntry);
    }
    else if (isNotManufacturableAssemblyPart(projectEntry)) {
        return articleEditDataForNotManufacturableAssemblyPart(projectEntry);
    }
    else {
        if (!isAssembly(projectEntry)) {
            throw Error("Wrong type.");
        }
        return articleEditDataForAssembly(projectEntry);
    }
}
